import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'

import { apiActions } from '../../redux'
import {
  createSocrClient,
  createSocrRelationship,
  deleteSocrClient,
  deleteSocrRelationship,
  getSocrClient,
  listSocrClients,
  listSocrRelationships,
  normalizeRepoUuid,
  replaceSocrBitbucket,
  replaceSocrGoogle,
  replaceSocrJira,
  replaceSocrQbo,
  updateSocrClient
} from '../../api/socrInventory'
import { SocrNavDrawer } from '../SocrNavDrawer'
import { SocrBoardStatusBar, resolveBoardAccent } from '../SocrBoardStatusBar'
import { emptyDraft } from './emptyDraft'
import { ClientCoreSection } from './ClientCoreSection'
import { ClientBitbucketSection } from './ClientBitbucketSection'
import { ClientJiraSection } from './ClientJiraSection'
import { ClientBillingSection } from './ClientBillingSection'
import { ClientRelationshipsSection } from './ClientRelationshipsSection'

const ClientOrgBoard = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const accent = resolveBoardAccent(theme, 'clientOrganization')
  const token = useSelector((state) => state.auth?.token)
  const socrCodeTree = useSelector((state) => state.api?.socrCodeTree || [])

  const [list, setList] = useState([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState(null)
  const [query, setQuery] = useState('')

  const [selectedId, setSelectedId] = useState(null)
  const selectedIdRef = useRef(null)
  selectedIdRef.current = selectedId
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)

  const [coreDraft, setCoreDraft] = useState(emptyDraft())
  const [coreDirty, setCoreDirty] = useState(false)
  const [savingCore, setSavingCore] = useState(false)
  const [savingLinks, setSavingLinks] = useState({})
  const [banner, setBanner] = useState(null)

  const [relationships, setRelationships] = useState([])
  const [savingRel, setSavingRel] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState(emptyDraft())
  const [creating, setCreating] = useState(false)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const refreshCatalogCaches = useCallback(() => {
    dispatch(apiActions.fetchSocrClients({ force: true }))
    dispatch(apiActions.fetchSocrCodeTree({ force: true }))
  }, [dispatch])

  const flash = useCallback((severity, message) => {
    setBanner({ severity, message })
  }, [])

  const loadList = useCallback(async ({ preferId } = {}) => {
    if (!token) {
      setListLoading(false)
      return
    }
    setListLoading(true)
    setListError(null)
    try {
      const rows = await listSocrClients(token)
      const sorted = (Array.isArray(rows) ? rows : [])
        .slice()
        .sort((a, b) => String(a.label || '').localeCompare(String(b.label || '')))
      setList(sorted)
      const currentId = selectedIdRef.current
      const nextId = preferId && sorted.some((c) => c.id === preferId)
        ? preferId
        : (currentId && sorted.some((c) => c.id === currentId) ? currentId : sorted[0]?.id || null)
      if (nextId !== currentId) setSelectedId(nextId)
    } catch (err) {
      setListError(err.message || 'Failed to list clients')
    } finally {
      setListLoading(false)
    }
  }, [token])

  const loadDetail = useCallback(async (id) => {
    if (!token || !id) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    setDetailError(null)
    try {
      const [client, allRels] = await Promise.all([
        getSocrClient(token, id),
        listSocrRelationships(token)
      ])
      setDetail(client)
      setCoreDraft({
        label: client.label || '',
        color: client.color || '#6b7280',
        zendeskTracking: Boolean(client.zendeskTracking),
        vulnTracking: Boolean(client.vulnTracking),
        upgradeTracking: Boolean(client.upgradeTracking),
        isActiveSupport: client.isActiveSupport !== false
      })
      setCoreDirty(false)
      setRelationships(Array.isArray(allRels) ? allRels : [])
    } catch (err) {
      setDetail(null)
      setDetailError(err.message || 'Failed to load client')
    } finally {
      setDetailLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    if (selectedId) loadDetail(selectedId)
  }, [selectedId, loadDetail])

  // Repo DT counts come from Redux code-tree (also wiped on hard reload)
  useEffect(() => {
    if (!token) return
    dispatch(apiActions.fetchSocrCodeTree({ force: false }))
  }, [dispatch, token])

  const filteredList = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter((c) =>
      [c.label, c.id].some((v) => String(v || '').toLowerCase().includes(q))
    )
  }, [list, query])

  const clientLabelById = useMemo(() => {
    const map = new Map()
    list.forEach((c) => map.set(c.id, c.label || c.id))
    return map
  }, [list])

  const clientRelationships = useMemo(() => {
    if (!selectedId) return []
    return relationships.filter(
      (r) => r.clientIdA === selectedId || r.clientIdB === selectedId
    )
  }, [relationships, selectedId])

  const peerOptions = useMemo(
    () => list.filter((c) => c.id !== selectedId),
    [list, selectedId]
  )

  const codeTreeReposByUuid = useMemo(() => {
    const map = {}
    ;(Array.isArray(socrCodeTree) ? socrCodeTree : []).forEach((client) => {
      ;(client?.repositories || []).forEach((repo) => {
        const uuid = normalizeRepoUuid(repo.repositoryUuid)
        if (uuid) map[uuid] = repo
      })
    })
    return map
  }, [socrCodeTree])

  const patchCore = (patch) => {
    setCoreDraft((prev) => ({ ...prev, ...patch }))
    setCoreDirty(true)
  }

  const saveCore = async () => {
    if (!token || !selectedId) return
    setSavingCore(true)
    try {
      const updated = await updateSocrClient(token, selectedId, {
        label: coreDraft.label.trim(),
        color: coreDraft.color,
        zendeskTracking: coreDraft.zendeskTracking,
        vulnTracking: coreDraft.vulnTracking,
        upgradeTracking: coreDraft.upgradeTracking,
        isActiveSupport: coreDraft.isActiveSupport
      })
      setDetail(updated)
      setCoreDirty(false)
      flash('success', 'Client settings saved')
      await loadList({ preferId: selectedId })
      refreshCatalogCaches()
    } catch (err) {
      flash('error', err.message || 'Save failed')
    } finally {
      setSavingCore(false)
    }
  }

  const withLinkSave = async (key, fn) => {
    setSavingLinks((prev) => ({ ...prev, [key]: true }))
    try {
      const updated = await fn()
      setDetail(updated)
      flash('success', 'Links updated')
      await loadList({ preferId: selectedId })
      refreshCatalogCaches()
      return updated
    } catch (err) {
      flash('error', err.message || 'Link update failed')
      throw err
    } finally {
      setSavingLinks((prev) => ({ ...prev, [key]: false }))
    }
  }

  const mapBitbucketPayload = (repos) =>
    (repos || []).map((r) => ({
      repositoryUuid: normalizeRepoUuid(r.repositoryUuid),
      repositoryName: r.repositoryName || r.repositorySlug || r.repositoryUuid,
      repositorySlug: r.repositorySlug || ''
    }))

  const linkBitbucket = async (repo) => {
    if (!detail || !repo) return
    const existing = Array.isArray(detail.bitbucketRepos) ? detail.bitbucketRepos : []
    const uuid = normalizeRepoUuid(repo.repositoryUuid)
    if (existing.some((r) => normalizeRepoUuid(r.repositoryUuid) === uuid)) {
      flash('info', 'Repository already linked')
      return
    }
    await withLinkSave('bitbucket', () =>
      replaceSocrBitbucket(token, selectedId, mapBitbucketPayload([...existing, repo]))
    )
  }

  const unlinkBitbucket = async (uuid) => {
    const existing = Array.isArray(detail?.bitbucketRepos) ? detail.bitbucketRepos : []
    const normalized = normalizeRepoUuid(uuid)
    await withLinkSave('bitbucket', () =>
      replaceSocrBitbucket(
        token,
        selectedId,
        mapBitbucketPayload(existing.filter((r) => normalizeRepoUuid(r.repositoryUuid) !== normalized))
      )
    )
  }

  const linkJira = async (link) => {
    if (!detail || !link?.jiraKey) return
    const existing = Array.isArray(detail.jiraProjects) ? detail.jiraProjects : []
    const key = String(link.jiraKey).toUpperCase()
    if (existing.some((j) => String(j.jiraKey || '').toUpperCase() === key)) {
      flash('info', `Jira ${key} is already linked`)
      return
    }
    await withLinkSave('jira', () =>
      replaceSocrJira(token, selectedId, [
        ...existing.map((j) => ({ jiraKey: j.jiraKey, jiraName: j.jiraName || j.jiraKey })),
        { jiraKey: key, jiraName: link.jiraName || key }
      ])
    )
  }

  const unlinkJira = async (jiraKeyToRemove) => {
    const existing = Array.isArray(detail?.jiraProjects) ? detail.jiraProjects : []
    await withLinkSave('jira', () =>
      replaceSocrJira(
        token,
        selectedId,
        existing
          .filter((j) => j.jiraKey !== jiraKeyToRemove)
          .map((j) => ({ jiraKey: j.jiraKey, jiraName: j.jiraName || j.jiraKey }))
      )
    )
  }

  const addRelationship = async ({ clientIdB, relationshipType, notes }) => {
    if (!token || !selectedId || !clientIdB) return
    setSavingRel(true)
    try {
      await createSocrRelationship(token, {
        clientIdA: selectedId,
        clientIdB,
        relationshipType,
        notes
      })
      const allRels = await listSocrRelationships(token)
      setRelationships(Array.isArray(allRels) ? allRels : [])
      flash('success', 'Relationship created')
      await loadList({ preferId: selectedId })
      refreshCatalogCaches()
    } catch (err) {
      flash('error', err.message || 'Could not create relationship')
    } finally {
      setSavingRel(false)
    }
  }

  const removeRelationship = async (id) => {
    if (!token || !id) return
    setSavingRel(true)
    try {
      await deleteSocrRelationship(token, id)
      setRelationships((prev) => prev.filter((r) => r.id !== id))
      flash('success', 'Relationship removed')
      await loadList({ preferId: selectedId })
      refreshCatalogCaches()
    } catch (err) {
      flash('error', err.message || 'Could not delete relationship')
    } finally {
      setSavingRel(false)
    }
  }

  const handleCreate = async () => {
    if (!token || !createDraft.label.trim()) return
    setCreating(true)
    try {
      const created = await createSocrClient(token, {
        label: createDraft.label.trim(),
        color: createDraft.color,
        zendeskTracking: createDraft.zendeskTracking,
        vulnTracking: createDraft.vulnTracking,
        upgradeTracking: createDraft.upgradeTracking,
        isActiveSupport: createDraft.isActiveSupport
      })
      setCreateOpen(false)
      setCreateDraft(emptyDraft())
      flash('success', `Created ${created.label}`)
      await loadList({ preferId: created.id })
      setSelectedId(created.id)
      refreshCatalogCaches()
    } catch (err) {
      flash('error', err.message || 'Create failed')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!token || !selectedId) return
    setDeleting(true)
    try {
      await deleteSocrClient(token, selectedId)
      setDeleteOpen(false)
      flash('success', 'Client deleted')
      setSelectedId(null)
      setDetail(null)
      await loadList()
      refreshCatalogCaches()
    } catch (err) {
      flash('error', err.message || 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  const listMeta = (client) => {
    const bits = [
      `J${client.jiraCount || 0}`,
      `B${client.bitbucketCount || 0}`,
      `Q${client.qboCount ?? (Array.isArray(client.qboCustomers) ? client.qboCustomers.length : 0)}`,
      `G${client.googleCount ?? (Array.isArray(client.googleProjects) ? client.googleProjects.length : 0)}`
    ]
    if (client.isActiveSupport === false) bits.push('inactive')
    return bits.join(' · ')
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
      <Box sx={{ borderBottom: `1px solid ${alpha('#fff', 0.1)}` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: { xs: 1, sm: 1.25 },
            py: 0.65,
            minHeight: 44
          }}
        >
          <SocrNavDrawer />
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', flex: 1 }} noWrap>
            Client Organization
          </Typography>
          <IconButton
            size='small'
            title='Refresh'
            onClick={() => {
              loadList({ preferId: selectedId })
              if (selectedId) loadDetail(selectedId)
              refreshCatalogCaches()
            }}
          >
            <RefreshIcon fontSize='small' />
          </IconButton>
          <Button
            size='small'
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ backgroundColor: accent, '&:hover': { backgroundColor: alpha(accent, 0.85) } }}
          >
            New client
          </Button>
        </Box>
        <SocrBoardStatusBar />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
          gap: 0,
          minHeight: 0
        }}
      >
        <Box
          sx={{
            borderRight: { md: `1px solid ${alpha('#fff', 0.1)}` },
            borderBottom: { xs: `1px solid ${alpha('#fff', 0.1)}`, md: 'none' },
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            maxHeight: { xs: 320, md: 'calc(100vh - 90px)' },
            overflow: 'hidden'
          }}
        >
          <TextField
            size='small'
            fullWidth
            placeholder='Search clients'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' sx={{ color: alpha('#fff', 0.45) }} />
                </InputAdornment>
              )
            }}
          />

          {listError ? <Alert severity='error'>{listError}</Alert> : null}

          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {listLoading
              ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </Box>
                )
              : filteredList.length
                ? (
                  <Stack spacing={0.5}>
                    {filteredList.map((client) => {
                      const active = client.id === selectedId
                      return (
                        <Box
                          key={client.id}
                          component='button'
                          type='button'
                          onClick={() => setSelectedId(client.id)}
                          sx={{
                            textAlign: 'left',
                            cursor: 'pointer',
                            border: `1px solid ${active ? alpha(accent, 0.55) : alpha('#fff', 0.08)}`,
                            borderRadius: 1.5,
                            px: 1.1,
                            py: 0.9,
                            backgroundColor: active ? alpha(accent, 0.16) : alpha('#000', 0.2),
                            color: 'inherit',
                            width: '100%'
                          }}
                        >
                          <Stack direction='row' spacing={1} alignItems='center'>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: client.color || '#6b7280',
                                flexShrink: 0
                              }}
                            />
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography noWrap sx={{ fontWeight: 700, fontSize: '0.84rem' }}>
                                {client.label}
                              </Typography>
                              <Typography noWrap variant='caption' sx={{ color: alpha('#fff', 0.55), fontWeight: 600 }}>
                                {listMeta(client)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      )
                    })}
                  </Stack>
                  )
                : (
                  <Typography variant='body2' sx={{ color: alpha('#fff', 0.5), p: 1 }}>
                    No clients match
                  </Typography>
                  )}
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            overflow: 'auto',
            maxHeight: { md: 'calc(100vh - 90px)' }
          }}
        >
          {banner
            ? (
              <Alert
                severity={banner.severity}
                sx={{ mb: 1.5 }}
                action={(
                  <IconButton size='small' color='inherit' onClick={() => setBanner(null)}>
                    <CloseIcon fontSize='small' />
                  </IconButton>
                )}
              >
                {banner.message}
              </Alert>
              )
            : null}

          {!selectedId
            ? (
              <Typography sx={{ color: alpha('#fff', 0.55) }}>
                Select a client to edit mappings and DT branch tools.
              </Typography>
              )
            : detailLoading
              ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
                )
              : detailError
                ? <Alert severity='error'>{detailError}</Alert>
                : detail
                  ? (
                    <Stack spacing={1.75}>
                      <ClientCoreSection
                        detail={detail}
                        coreDraft={coreDraft}
                        coreDirty={coreDirty}
                        savingCore={savingCore}
                        onPatch={patchCore}
                        onSave={saveCore}
                        onDelete={() => setDeleteOpen(true)}
                      />

                      <ClientBitbucketSection
                        token={token}
                        repos={detail.bitbucketRepos || []}
                        saving={Boolean(savingLinks.bitbucket)}
                        codeTreeReposByUuid={codeTreeReposByUuid}
                        onLinkRepos={linkBitbucket}
                        onUnlink={unlinkBitbucket}
                        onInventoryChanged={refreshCatalogCaches}
                        onFlash={flash}
                      />

                      <ClientJiraSection
                        token={token}
                        projects={detail.jiraProjects || []}
                        saving={Boolean(savingLinks.jira)}
                        onLink={linkJira}
                        onUnlink={unlinkJira}
                      />

                      <ClientBillingSection
                        qboCustomers={detail.qboCustomers || []}
                        googleProjects={detail.googleProjects || []}
                        savingQbo={Boolean(savingLinks.qbo)}
                        savingGoogle={Boolean(savingLinks.google)}
                        onSaveQbo={(customers) =>
                          withLinkSave('qbo', () => replaceSocrQbo(token, selectedId, customers))}
                        onSaveGoogle={(ids) =>
                          withLinkSave('google', () => replaceSocrGoogle(token, selectedId, ids))}
                      />

                      <ClientRelationshipsSection
                        selectedId={selectedId}
                        relationships={clientRelationships}
                        peerOptions={peerOptions}
                        clientLabelById={clientLabelById}
                        saving={savingRel}
                        onAdd={addRelationship}
                        onRemove={removeRelationship}
                      />
                    </Stack>
                    )
                  : null}
        </Box>
      </Box>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>New client</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextField
              size='small'
              label='Label'
              autoFocus
              fullWidth
              value={createDraft.label}
              onChange={(e) => setCreateDraft((d) => ({ ...d, label: e.target.value }))}
            />
            <TextField
              size='small'
              label='Color'
              value={createDraft.color}
              onChange={(e) => setCreateDraft((d) => ({ ...d, color: e.target.value }))}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createDraft.isActiveSupport}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, isActiveSupport: e.target.checked }))}
                />
              )}
              label='Active support'
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createDraft.zendeskTracking}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, zendeskTracking: e.target.checked }))}
                />
              )}
              label='Zendesk'
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createDraft.vulnTracking}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, vulnTracking: e.target.checked }))}
                />
              )}
              label='Vuln board'
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={createDraft.upgradeTracking}
                  onChange={(e) => setCreateDraft((d) => ({ ...d, upgradeTracking: e.target.checked }))}
                />
              )}
              label='Upgrades board'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button
            variant='contained'
            disabled={creating || !createDraft.label.trim()}
            onClick={handleCreate}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Delete client?</DialogTitle>
        <DialogContent>
          <Typography>
            This removes <strong>{detail?.label}</strong> and cascaded link / relationship rows for that client.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color='error' variant='contained' disabled={deleting} onClick={handleDelete}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export { ClientOrgBoard }
